import React, {useCallback, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {NoResults} from 'components/SearchUnavailability/NoResults'
import {VisibilitySensor} from 'components/VisibilitySensor'
import {getPaginationContext} from 'middleware/analytics/selectors'

import {Button, buttonSizesMap} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Loader} from '@vio/atlas/Loader'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {PaginationState} from '../usePagination'

const BUTTON_SIZE = buttonSizesMap.lg.size
const SPINNER_SIZE = 24

const LoaderWrapper = styled.div`
  padding: ${(BUTTON_SIZE - SPINNER_SIZE) / 2}px 0;
`

const NoResultsWrapper = styled.div`
  margin-top: 40px;
  margin-bottom: 60px;
`

type PaginationFooterProps = {
  paginationState: PaginationState
  onLoadMore: () => void
}

export const PaginationFooter = ({
  paginationState,
  onLoadMore
}: PaginationFooterProps) => {
  const dispatch = useDispatch()
  const [noMoreSensorIsEnabled, setNoMoreSensorIsEnabled] = useState(true)
  const paginationContext = useSelector(getPaginationContext)

  const onNoMoreResultsMessageView = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.SearchResultsEnd,
        action: Action.Viewed,
        analyticsContext: {
          [AnalyticsContext.PaginationContext]: paginationContext
        }
      })
    )
    setNoMoreSensorIsEnabled(false)
  }, [dispatch, paginationContext])

  if (paginationState === PaginationState.Loading)
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    )

  if (paginationState === PaginationState.HasMore)
    return (
      <Box display="flex" justifyContent="center">
        <Button
          variant="tertiary"
          dataId="LoadMoreButton"
          onClick={onLoadMore}
          iconEnd={<Icon name="ArrowDown" />}
        >
          <FormattedMessageWrapper
            id="loadMore"
            dataId="loadMore"
            defaultMessage="Load more"
          />
        </Button>
      </Box>
    )

  if (paginationState === PaginationState.NoMore) {
    return (
      <VisibilitySensor
        partialVisibility="bottom"
        active={noMoreSensorIsEnabled}
        onChange={isVisible => isVisible && onNoMoreResultsMessageView()}
      >
        <NoResultsWrapper>
          <NoResults noMoreResults />
        </NoResultsWrapper>
      </VisibilitySensor>
    )
  }

  return null
}
